import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import type { FiatCurrency } from '#src/__generated__/globalTypes.ts';
import { Cryptocurrency, Currency } from '#src/__generated__/globalTypes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { withFragments } from '#src/lib/gql/index.ts';
import { SOL_DECIMAL_PLACES } from '#src/lib/lamport.ts';
import type { MonetaryAmountParams } from '#src/lib/monetaryAmount.ts';
import { MonetaryAmount } from '#src/lib/monetaryAmount.ts';
import { ETH_DECIMAL_PLACES } from '#src/lib/wei.ts';

import type { useAmountWithConversion_monetaryAmount } from './__generated__/useAmountWithConversion.graphql.ts';
import { useMonetaryAmount } from './useMonetaryAmount.ts';

export type Props = {
  monetaryAmount: MonetaryAmountParams | MonetaryAmount | null | undefined;
  primaryCurrency?: Currency;
  hideFractionDigits?: boolean;
  hideSymbol?: boolean;
  useGrouping?: boolean;
  forceFiatCurrency?: FiatCurrency;
  forceCryptocurrency?: Cryptocurrency;
};

export const useAmountWithConversion = withFragments(
  ({
    monetaryAmount,
    primaryCurrency,
    hideFractionDigits,
    hideSymbol,
    useGrouping = true,
    forceFiatCurrency,
    forceCryptocurrency,
  }: Props) => {
    const { formatNumber, formatWei, formatLamport } = useIntlContext();
    const { toMonetaryAmount } = useMonetaryAmount();
    const {
      cryptocurrency: userCryptocurrency,
      fiatCurrency: userFiatCurrency,
      currency: userCurrency,
      walletPreferences: { onlyShowFiatCurrency },
    } = useCurrentUserContext();

    const cryptocurrency = forceCryptocurrency || userCryptocurrency;
    const fiatCurrency = forceFiatCurrency || userFiatCurrency;

    const fullMonetaryAmount =
      monetaryAmount instanceof MonetaryAmount
        ? monetaryAmount
        : toMonetaryAmount(monetaryAmount);

    const fiatAmount = fullMonetaryAmount.toMajorUnit(fiatCurrency);
    const fiatFormatted = formatNumber(fiatAmount, {
      style: 'currency',
      currency: fiatCurrency,
      ...(hideFractionDigits ? { maximumFractionDigits: 0 } : {}),
    });

    const cryptoAmount = (() => {
      switch (cryptocurrency) {
        case Cryptocurrency.LAMPORT:
          return fullMonetaryAmount.lamport;
        case Cryptocurrency.WEI:
        default:
          return fullMonetaryAmount.wei;
      }
    })();
    const cryptoFormatted = (() => {
      switch (cryptocurrency) {
        case Cryptocurrency.WEI:
          return formatWei(
            cryptoAmount,
            undefined,
            {
              maximumFractionDigits: ETH_DECIMAL_PLACES,
              useGrouping,
            },
            { hideSymbol }
          );
        case Cryptocurrency.LAMPORT:
        default:
          return formatLamport(
            cryptoAmount,
            undefined,
            {
              maximumFractionDigits: SOL_DECIMAL_PLACES,
              useGrouping,
            },
            { hideSymbol }
          );
      }
    })();

    const actualPrimaryCurrency = primaryCurrency || userCurrency;

    const exponent =
      actualPrimaryCurrency === Currency.ETH ? fiatFormatted : cryptoFormatted;

    return {
      main:
        actualPrimaryCurrency === Currency.ETH
          ? cryptoFormatted
          : fiatFormatted,
      exponent:
        primaryCurrency === Currency.ETH || !onlyShowFiatCurrency
          ? exponent
          : null,
      cryptoAmount,
    };
  },
  {
    monetaryAmount: gql`
      fragment useAmountWithConversion_monetaryAmount on MonetaryAmount {
        ...useMonetaryAmount_monetaryAmount
      }
      ${useMonetaryAmount.fragments.monetaryAmount}
    ` as TypedDocumentNode<useAmountWithConversion_monetaryAmount>,
  }
);
